import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clubsService, eventsService } from '../../services';
import { useUser } from '../../UserContext';
import './StandLaunch.css';

const toLocalDateInputValue = (date = new Date()) => {
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().split('T')[0];
};

const toLocalTimeInputValue = (date = new Date()) => date.toTimeString().slice(0, 5);

const buildSuggestedEventName = (stand) => {
    const todayLabel = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    });
    const disciplineLabel = stand?.discipline || stand?.variant || 'Clay';
    return `${disciplineLabel} Session - ${todayLabel}`;
};

const StandLaunch = () => {
    const { standCode } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();
    const [stand, setStand] = useState(null);
    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreatingEvent, setIsCreatingEvent] = useState(false);
    const [createEventError, setCreateEventError] = useState('');
    const [showCreateEventForm, setShowCreateEventForm] = useState(false);
    const [createEventForm, setCreateEventForm] = useState({
        name: '',
        eventDate: toLocalDateInputValue(),
        eventTime: toLocalTimeInputValue()
    });

    useEffect(() => {
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const standData = await clubsService.getStandLaunchByCode(standCode);
                setStand(standData);

                const currentUserId = Number(user?.userId || user?.id || 0);
                const clayEvents = await eventsService.getEvents('Clay');
                const matchingEvents = Array.isArray(clayEvents)
                    ? clayEvents.filter((event) =>
                        Number(event.clubId) === Number(standData.clubId)
                        && Number(event.userId) === currentUserId
                    )
                    : [];

                setEvents(matchingEvents);
                if (matchingEvents.length === 1) {
                    setSelectedEventId(String(matchingEvents[0].eventId));
                }
                setShowCreateEventForm(matchingEvents.length === 0);
                setCreateEventForm({
                    name: buildSuggestedEventName(standData),
                    eventDate: toLocalDateInputValue(),
                    eventTime: toLocalTimeInputValue()
                });
            } catch (loadError) {
                console.error(loadError);
                setError('Failed to load this stand.');
            } finally {
                setIsLoading(false);
            }
        };

        load();
    }, [standCode, user?.id, user?.userId]);

    const heroPhoto = useMemo(() => stand?.photos?.[0]?.photoUrl || null, [stand]);

    const launch = (mode) => {
        if (!selectedEventId) {
            setError('Choose an event first.');
            return;
        }

        navigate(`/dashboard/score/clay?eventId=${selectedEventId}&standId=${stand.standId}&launch=${mode}`);
    };

    const handleCreateEventFieldChange = (event) => {
        const { name, value } = event.target;
        setCreateEventForm((prev) => ({ ...prev, [name]: value }));
        setCreateEventError('');
    };

    const handleCreateEvent = async () => {
        if (!stand?.clubId || isCreatingEvent) {
            return;
        }

        if (!createEventForm.name.trim()) {
            setCreateEventError('Add a name for your event first.');
            return;
        }

        setIsCreatingEvent(true);
        setCreateEventError('');

        try {
            const createdEvent = await eventsService.createEvent({
                clubId: stand.clubId,
                name: createEventForm.name.trim(),
                eventDate: createEventForm.eventDate,
                eventTime: createEventForm.eventTime,
                eventType: 'Clay'
            });

            setEvents((prev) => [createdEvent, ...prev]);
            setSelectedEventId(String(createdEvent.eventId));
            setShowCreateEventForm(false);
        } catch (createError) {
            console.error(createError);
            setCreateEventError(createError?.message || 'We could not create your event just yet.');
        } finally {
            setIsCreatingEvent(false);
        }
    };

    if (isLoading) {
        return <div className="stand-launch-page"><div className="stand-launch-card"><p>Loading stand...</p></div></div>;
    }

    if (error || !stand) {
        return <div className="stand-launch-page"><div className="stand-launch-card"><p>{error || 'Stand not found.'}</p></div></div>;
    }

    return (
        <div className="stand-launch-page">
            <div className="stand-launch-card">
                <button className="stand-launch-back" onClick={() => navigate('/dashboard/score/clay')}>
                    Back to Clay Scoring
                </button>

                <div className="stand-launch-hero">
                    <div>
                        <p className="stand-launch-kicker">Stand QR</p>
                        <h1>{stand.name}</h1>
                        <p className="stand-launch-copy">
                            {stand.description || 'View the stand setup, then jump straight into scoring.'}
                        </p>
                        {stand.standAccessCode ? (
                            <div className="stand-launch-code-banner">
                                <span>Stand code</span>
                                <strong>{stand.standAccessCode}</strong>
                            </div>
                        ) : null}
                        <div className="stand-launch-tags">
                            {stand.discipline && <span>{stand.discipline}</span>}
                            {stand.variant && <span>{stand.variant}</span>}
                            {stand.standType && <span>{stand.standType}</span>}
                        </div>
                    </div>
                    {heroPhoto && <img src={heroPhoto} alt={stand.name} className="stand-launch-hero-image" />}
                </div>

                <div className="stand-launch-section">
                    <h2>Shoot This Stand</h2>
                    {events.length > 0 ? (
                        <>
                            <p>Pick one of your clay events at this venue, then choose how you want to score it.</p>
                            <select
                                value={selectedEventId}
                                onChange={(event) => setSelectedEventId(event.target.value)}
                                className="stand-launch-select"
                            >
                                <option value="">Select an event</option>
                                {events.map((event) => (
                                    <option key={event.eventId} value={event.eventId}>
                                        {event.name} - {new Date(event.eventDate).toLocaleDateString('en-GB')}
                                    </option>
                                ))}
                            </select>
                            <div className="stand-launch-actions">
                                <button className="stand-launch-primary" onClick={() => launch('fun')}>
                                    Start Fun Shoot
                                </button>
                                <button className="stand-launch-secondary" onClick={() => launch('competitive')}>
                                    Start Competitive
                                </button>
                                <button
                                    className="stand-launch-secondary"
                                    onClick={() => {
                                        setShowCreateEventForm((prev) => !prev);
                                        setCreateEventError('');
                                    }}
                                >
                                    {showCreateEventForm ? 'Hide Create Event' : 'Create Event'}
                                </button>
                            </div>
                            {showCreateEventForm ? (
                                <div className="stand-launch-inline-create">
                                    <div className="stand-launch-quick-create">
                                        <div className="stand-launch-quick-create-grid">
                                            <label>
                                                <span>Event name</span>
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={createEventForm.name}
                                                    onChange={handleCreateEventFieldChange}
                                                    placeholder="My clay session"
                                                />
                                            </label>
                                            <label>
                                                <span>Date</span>
                                                <input
                                                    type="date"
                                                    name="eventDate"
                                                    value={createEventForm.eventDate}
                                                    onChange={handleCreateEventFieldChange}
                                                />
                                            </label>
                                            <label>
                                                <span>Time</span>
                                                <input
                                                    type="time"
                                                    name="eventTime"
                                                    value={createEventForm.eventTime}
                                                    onChange={handleCreateEventFieldChange}
                                                />
                                            </label>
                                        </div>
                                        {createEventError ? <p className="stand-launch-inline-error">{createEventError}</p> : null}
                                        <div className="stand-launch-actions">
                                            <button className="stand-launch-primary" onClick={handleCreateEvent} disabled={isCreatingEvent}>
                                                {isCreatingEvent ? 'Creating Event...' : 'Create Event'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    ) : (
                        <div className="stand-launch-empty-state">
                            <div>
                                <h3>You do not have an event at this venue yet</h3>
                                <p>Create your own clay event here and we will keep you on this stand ready to score.</p>
                            </div>
                            <div className="stand-launch-quick-create">
                                <div className="stand-launch-quick-create-grid">
                                    <label>
                                        <span>Event name</span>
                                        <input
                                            type="text"
                                            name="name"
                                            value={createEventForm.name}
                                            onChange={handleCreateEventFieldChange}
                                            placeholder="My clay session"
                                        />
                                    </label>
                                    <label>
                                        <span>Date</span>
                                        <input
                                            type="date"
                                            name="eventDate"
                                            value={createEventForm.eventDate}
                                            onChange={handleCreateEventFieldChange}
                                        />
                                    </label>
                                    <label>
                                        <span>Time</span>
                                        <input
                                            type="time"
                                            name="eventTime"
                                            value={createEventForm.eventTime}
                                            onChange={handleCreateEventFieldChange}
                                        />
                                    </label>
                                </div>
                                {createEventError ? <p className="stand-launch-inline-error">{createEventError}</p> : null}
                                <div className="stand-launch-actions">
                                    <button className="stand-launch-primary" onClick={handleCreateEvent} disabled={isCreatingEvent}>
                                        {isCreatingEvent ? 'Creating Event...' : 'Create Event'}
                                    </button>
                                    <button className="stand-launch-secondary" onClick={() => navigate('/dashboard/score/clay')}>
                                        Back to Clay Scoring
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="stand-launch-section">
                    <h2>Stand Photos</h2>
                    {(stand.photos || []).length === 0 ? (
                        <p>No stand photos yet.</p>
                    ) : (
                        <div className="stand-launch-media-grid">
                            {stand.photos.map((photo) => (
                                <figure key={photo.photoId} className="stand-launch-media-card">
                                    <img src={photo.photoUrl} alt={photo.description || stand.name} />
                                    {photo.description && <figcaption>{photo.description}</figcaption>}
                                </figure>
                            ))}
                        </div>
                    )}
                </div>

                <div className="stand-launch-section">
                    <h2>Stand Videos</h2>
                    {(stand.videos || []).length === 0 ? (
                        <p>No stand videos yet.</p>
                    ) : (
                        <div className="stand-launch-video-list">
                            {stand.videos.map((video) => (
                                <a key={video.videoId} href={video.videoUrl} target="_blank" rel="noreferrer" className="stand-launch-video-link">
                                    <strong>{video.description || 'Stand video'}</strong>
                                    <span>{video.videoUrl}</span>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StandLaunch;
